import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { useAuth } from "@/lib/auth";
import { Database, FileText, FileJson, ShieldCheck } from "lucide-react";

const LABELS = {
  sites: "Sites",
  trucks: "Trucks",
  drivers: "Drivers",
  projects: "Projects",
  haulers: "Haulers",
  loads: "Loads (with custody events)",
  invoices: "Invoices (with line items)",
  audit_log: "Audit Log",
  users: "Users (password hashes excluded)",
};

const DESCRIPTIONS = {
  sites: "Construction, dump-yard and fill-site directory",
  trucks: "Fleet with plate, capacity, DOT, tare weight, hauler linkage",
  drivers: "CDL roster with expiries and hauler linkage",
  projects: "Capital projects with budgets and approved-drop whitelist",
  haulers: "Registered carriers with insurance and approval state",
  loads: "All haul jobs with full chain-of-custody trail",
  invoices: "Billing records with line items and totals",
  audit_log: "Immutable trail — every create/update/delete/approve/export",
  users: "User accounts and roles (safe — no password hashes)",
};

export default function Export() {
  const { user } = useAuth();
  const [summary, setSummary] = useState([]);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/export/summary");
      setSummary(data);
      setError("");
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const isAdmin = user?.role === "admin";

  if (!isAdmin) {
    return (
      <div>
        <PageHeader title="Data Export" subtitle="Backup & audit" />
        <div className="p-8">
          <div className="bg-white border border-gray-200 p-8 text-center">
            <ShieldCheck className="w-8 h-8 text-gray-400 mx-auto mb-3" />
            <div className="text-sm text-gray-600">Admin access required.</div>
          </div>
        </div>
      </div>
    );
  }

  const backend = process.env.REACT_APP_BACKEND_URL;

  return (
    <div>
      <PageHeader
        title="Data Export"
        subtitle="Backup & audit"
        actions={
          <div className="flex items-center gap-2 text-xs font-mono text-gray-600">
            <Database className="w-4 h-4 text-emerald-600" />
            <span>{summary.reduce((s, r) => s + r.count, 0).toLocaleString()} total rows</span>
          </div>
        }
      />
      <div className="p-8">
        {error && <div className="mb-4 px-3 py-2 bg-red-50 border border-red-200 text-red-800 text-sm">{error}</div>}

        <div className="mb-6 p-4 bg-white border border-gray-200">
          <div className="text-sm text-gray-700 leading-relaxed">
            Download a full snapshot of any collection as <strong>CSV</strong> (opens in Excel/Sheets) or <strong>JSON</strong> (structured, includes nested arrays like custody events and invoice line items). Every export writes a record to the Audit Log.
          </div>
        </div>

        <div className="space-y-3" data-testid="export-list">
          {summary.map((row) => (
            <div key={row.collection} className="bg-white border border-gray-200 p-5 flex items-center justify-between" data-testid={`export-row-${row.collection}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-3">
                  <div className="font-display font-bold text-gray-900 text-base">{LABELS[row.collection] || row.collection}</div>
                  <span className="font-mono text-xs px-2 py-0.5 bg-gray-100 text-gray-700">{row.count.toLocaleString()} rows</span>
                </div>
                <div className="text-xs text-gray-500 mt-1">{DESCRIPTIONS[row.collection]}</div>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={`${backend}/api/export/${row.collection}?format=csv`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`export-csv-${row.collection}`}
                  className={`flex items-center gap-1.5 px-3 py-2 border text-[11px] uppercase tracking-widest font-semibold transition-colors ${
                    row.count === 0
                      ? "border-gray-200 text-gray-400 pointer-events-none"
                      : "border-gray-300 text-gray-800 hover:bg-gray-900 hover:text-white hover:border-gray-900"
                  }`}
                >
                  <FileText className="w-3.5 h-3.5" /> CSV
                </a>
                <a
                  href={`${backend}/api/export/${row.collection}?format=json`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`export-json-${row.collection}`}
                  className={`flex items-center gap-1.5 px-3 py-2 border text-[11px] uppercase tracking-widest font-semibold transition-colors ${
                    row.count === 0
                      ? "border-gray-200 text-gray-400 pointer-events-none"
                      : "border-orange-500 text-orange-700 bg-orange-50 hover:bg-orange-500 hover:text-white"
                  }`}
                >
                  <FileJson className="w-3.5 h-3.5" /> JSON
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
